import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  theme1,
  theme2,
  themePalettes,
  applyThemeCssVariables,
  getCurrentThemeMode,
  setCurrentThemeMode,
} from "../../theme";

export const ThemeContext = createContext({
  themeMode: "theme1",
  setThemeMode: () => { },
  toggleTheme: () => { },
  theme: theme1,
  isTheme2: false,
  palette: themePalettes.theme1,
});

export const ThemeContextProvider = ({ children }) => {
  const [themeMode, setLocalThemeMode] = useState(() => {
    return getCurrentThemeMode();
  });

  useEffect(() => {
    applyThemeCssVariables(themeMode);
    if (typeof window !== "undefined") {
      window.__THEME__ = themeMode;
    }
  }, [themeMode]);

  useEffect(() => {
    const handleThemeChange = (e) => {
      const newTheme = e.detail?.theme;
      if (newTheme && (newTheme === "theme1" || newTheme === "theme2")) {
        setLocalThemeMode(newTheme);
      }
    };
    window.addEventListener("app-theme-changed", handleThemeChange);
    return () => {
      window.removeEventListener("app-theme-changed", handleThemeChange);
    };
  }, []);

  const setTheme = useCallback((mode) => {
    if (mode === "theme1" || mode === "theme2") {
      setLocalThemeMode(mode);
      setCurrentThemeMode(mode);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(themeMode === "theme1" ? "theme2" : "theme1");
  }, [themeMode, setTheme]);

  const activeTheme = useMemo(() => {
    return themeMode === "theme2" ? theme2 : theme1;
  }, [themeMode]);

  const activePalette = useMemo(() => {
    return themeMode === "theme2" ? themePalettes.theme2 : themePalettes.theme1;
  }, [themeMode]);

  const value = useMemo(
    () => ({
      themeMode,
      setThemeMode: setTheme,
      toggleTheme,
      theme: activeTheme,
      isTheme2: themeMode === "theme2",
      palette: activePalette,
    }),
    [themeMode, setTheme, toggleTheme, activeTheme, activePalette]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

ThemeContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useColorTheme = () => useContext(ThemeContext);
export default ThemeContextProvider;
